import React, { useEffect, useMemo } from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { useProjectTeamMembers } from '../hooks/useProjectTeamMembers';
import { useScheduleTask } from '../hooks/useScheduleTask';
import { TASK_LIMITS } from '../constants/projectConstants';
import { FormField, Input, Select, Textarea } from "@/components/ui/FormComponents";
import { formatInstallationTrade } from '../../verve-teams/utils/teamMemberLabels';
import Modal from "@/components/ui/Modal";

export const ScheduleTaskModal = ({ isOpen, onClose, project, onSuccess, eligibleMembers, taskToEdit = null }) => {
  const { members: allMembers } = useProjectTeamMembers(project);
  const fieldOfficers = eligibleMembers || allMembers;
  const { 
    form, 
    formError, 
    isSubmitting, 
    handleFormSubmit, 
    resetForm,
    formatLocalYMD 
  } = useScheduleTask(project, onClose, onSuccess, taskToEdit);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = form;

  const todayYmd = useMemo(() => formatLocalYMD(new Date()), [isOpen, formatLocalYMD]);
  const startDateWatch = watch('startDate');
  const descriptionWatch = watch('description');
  const descLen = typeof descriptionWatch === 'string' ? descriptionWatch.length : 0;
  const endDateMin = startDateWatch && startDateWatch >= todayYmd ? startDateWatch : todayYmd;

  // Polinaidu: 10-05-2026 - Simplified effect to avoid reset race conditions
  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  const modalTitle = (
    <div className="flex items-center gap-4">
      <div className="flex flex-col">
        <h2 className="text-[16px] font-semibold text-slate-800 tracking-tight leading-none">
          {taskToEdit ? 'Edit Project Task' : 'Schedule Project Task'}
        </h2>
        <span className="text-[12px] font-semibold text-blue-400   tracking-tight mt-1.5"> {project?.projectId || 'n/a'}</span>
      </div>
    </div>
  );

  const modalFooter = (
    <div className="flex items-center justify-end gap-3 w-full mt-4 pt-4 border-t border-slate-50">
      <button 
        type="button"
        onClick={onClose}
        className="px-6 py-2.5 bg-white border border-slate-200 text-slate-500 rounded-lg text-[12px] tracking-wider cursor-pointer hover:bg-slate-50 transition-all shadow-sm"
      >
        Cancel
      </button>
      <button 
        type="submit"
        form="schedule-task-form"
        disabled={isSubmitting || fieldOfficers.length === 0}
        className="px-8 py-2.5 bg-[#64748b] hover:bg-[#475569] text-white rounded-lg text-[12px] cursor-pointer tracking-wider transition-all disabled:opacity-50 disabled:grayscale flex items-center gap-3 shadow-md shadow-blue-200"
      >
        {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (taskToEdit ? 'Update Task' : 'Schedule Task')}
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      size="lg"
      footer={modalFooter}
    >
      <form 
        id="schedule-task-form"
        onSubmit={handleSubmit(handleFormSubmit)} 
        className="flex flex-col gap-6 font-poppins"
      >
        <FormField label="Task Name" error={errors.taskName} required>
          <Input
            {...register('taskName')}
            id="schedule-task-name"
            placeholder="e.g., Roof mounting installation"
            className="text-[13px] font-medium"
          />
        </FormField>

        <FormField label="Description" error={errors.description}>
          <Textarea
            {...register('description')}
            id="schedule-task-description"
            placeholder="Describe what needs to be done…"
            className="min-h-[100px] text-[13px] font-medium leading-relaxed"
          />
        </FormField>

        <div className="grid grid-cols-2 gap-6">
          <FormField label="Start date" error={errors.startDate} required>
            <Input
              {...register('startDate')}
              id="schedule-task-start-date"
              type="date"
              min={todayYmd}
              className="text-[13px] font-medium"
            />
            <p className="text-[10px] font-bold text-slate-400 mt-1.5 ml-0.5">Today or a future date</p>
          </FormField>

          <FormField label="Estimated end date" error={errors.estimatedEndDate} required>
            <Input
              {...register('estimatedEndDate')}
              id="schedule-task-end-date"
              type="date"
              min={endDateMin}
              className="text-[13px] font-medium"
            />
            <p className="text-[10px] font-bold text-slate-400 mt-1.5 ml-0.5">Must be on or after start date</p>
          </FormField>
        </div>

        <FormField label="Assign to installation member" error={errors.assignedOfficer} required>
          <div className="relative">
            <select 
              {...register('assignedOfficer')}
              id="schedule-task-assignee" 
              className="w-full h-[35px] px-3 text-[13px] font-medium border border-slate-200 rounded-md text-slate-800 appearance-none cursor-pointer focus:outline-none focus:ring-1 focus:border-brand-blue transition-all bg-white"
            >
              <option value="" hidden>Select installation member…</option>
              {fieldOfficers.map((officer) => (
                <option key={officer._id} value={officer._id}>
                  {officer.name ||
                    `${officer.firstName || ''} ${officer.lastName || ''}`.trim() ||
                    officer.email}
                  {officer.installationTrade ? ` · ${officer.installationTrade}` : ''}
                </option>
              ))}
            </select>
            <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
              <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
            </div>
          </div>
          <p className="text-[10px] font-bold text-slate-400 mt-2 ml-1 leading-relaxed">
            Select an installation member from the project team to assign this execution task.
          </p>
          {isOpen && project?.fieldOfficers?.length > 0 && fieldOfficers.length === 0 && (
            <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-100">
              <p className="text-[10px] font-bold text-amber-700 leading-normal">
                No eligible members yet — each trade must confirm material received on the project first.
              </p>
            </div>
          )}
        </FormField>

        {formError && (
          <div className="rounded-lg border border-rose-100 bg-rose-50 px-4 py-3 text-[11px] font-bold text-rose-700 animate-in fade-in slide-in-from-top-1">
            {formError}
          </div>
        )}
      </form>
    </Modal>
  );
};
